import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { ResultsView } from "@/components/ResultsView";
import { PestReport } from "@/components/PestReport";
import { Button } from "@/components/ui/button";
import { listOffline, removeOffline } from "@/lib/offline";
import { downloadReport, downloadPestReport } from "@/lib/report";
import { Smartphone, Trash2, Wifi, WifiOff, FileText, ChevronLeft, Sprout, Bug } from "lucide-react";

export default function Offline() {
  const [items, setItems] = useState(listOffline());
  const [open, setOpen] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const remove = (id) => { setItems(removeOffline(id)); if (open?.id === id) setOpen(null); };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950 tracking-tight flex items-center gap-3"><Smartphone className="h-8 w-8 text-[#2D6A4F]" /> On this phone</h1>
            <p className="text-stone-600 mt-1">Treatment sheets and soil reports saved here open without any signal.</p>
          </div>
          <span data-testid="online-status" className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${online ? "bg-[#EAF6EF] text-[#2D6A4F]" : "bg-amber-100 text-amber-800"}`}>
            {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />} {online ? "Online" : "Offline — showing saved copies"}
          </span>
        </div>

        {open ? (
          <div className="space-y-4" data-testid="offline-open">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Button data-testid="offline-back-btn" variant="ghost" onClick={() => setOpen(null)} className="text-[#2D6A4F]"><ChevronLeft className="h-4 w-4 mr-1" /> All saved items</Button>
              <Button data-testid="offline-print-btn" variant="outline" className="border-stone-300"
                onClick={() => (open.kind === "pest" ? downloadPestReport(open.payload) : downloadReport({ name: open.title, location: open.payload.location, soil: open.payload.soil, analysis: open.payload.analysis }))}>
                <FileText className="h-4 w-4 mr-2" /> Print / PDF
              </Button>
            </div>
            {open.kind === "pest" ? <PestReport report={open.payload} readOnly /> : <ResultsView analysis={open.payload.analysis} />}
          </div>
        ) : items.length === 0 ? (
          <div data-testid="offline-empty" className="bg-white rounded-2xl border border-dashed border-stone-300 p-12 text-center">
            <Smartphone className="h-10 w-10 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-600">Nothing saved yet. Use <b>Save to phone</b> on any soil report or pest diagnosis.</p>
          </div>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-4" data-testid="offline-list">
            {items.map((it) => (
              <li key={it.id} data-testid={`offline-item-${it.id}`} className="bg-white rounded-2xl border border-stone-200 p-5 flex items-start justify-between gap-3">
                <button onClick={() => setOpen(it)} className="text-left min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {it.kind === "pest" ? <Bug className="h-4 w-4 text-[#78350F]" /> : <Sprout className="h-4 w-4 text-[#2D6A4F]" />}
                    <span className="text-[10px] uppercase tracking-wider font-bold text-stone-500">{it.kind === "pest" ? "Treatment sheet" : "Soil report + calendar"}</span>
                  </div>
                  <div className="font-display font-bold text-emerald-950 truncate">{it.title}</div>
                  <div className="text-xs text-stone-500 mt-1">{it.subtitle} · saved {new Date(it.saved_at).toLocaleDateString()}</div>
                </button>
                <button data-testid={`offline-remove-${it.id}`} onClick={() => remove(it.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
